import { Link } from 'react-router-dom';
import { Award, Clock3, Gamepad2, Server, StickyNote, Vote } from 'lucide-react';
import type { ComponentType } from 'react';
import type { PlayerStats } from '../../types/player';
import { asCount, compactPlaytime, formatJoinAgo } from '../../utils/staffPlayer';

interface StaffPlayerFactsProps {
  uuid: string;
  stats: PlayerStats;
}

interface Fact {
  label: string;
  value: string;
  sub?: string;
  icon: ComponentType<{ className?: string }>;
  href?: string;
}

/** Minecraft-side facts for a linked staff account, pulled from the Users API. */
export const StaffPlayerFacts = ({ uuid, stats }: StaffPlayerFactsProps) => {
  const facts: Fact[] = [
    { label: 'Playtime', value: compactPlaytime(stats.playtime), icon: Clock3 },
    {
      label: 'Last seen',
      value: stats.lastjoinedservername || '—',
      sub: stats.lastjoinedtime ? formatJoinAgo(stats.lastjoinedtime) : undefined,
      icon: Gamepad2,
    },
    { label: 'Servers seen', value: String(asCount(stats.uniqueserversseenon)), icon: Server },
    {
      label: 'Notes · warnings',
      value: `${asCount(stats.numberofnotes)} · ${asCount(stats.numberofwarns)}`,
      icon: StickyNote,
      href: `/player/${uuid}`,
    },
    { label: 'Votes', value: String(asCount(stats.votes)), icon: Vote },
    { label: 'Awards', value: String(stats.player_awards?.length ?? 0), icon: Award },
  ];

  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
      {facts.map((fact) => {
        const Icon = fact.icon;
        const body = (
          <>
            <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-glass-500">
              <Icon className="w-3 h-3" aria-hidden="true" />
              {fact.label}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-white tabular-nums truncate" title={fact.value}>
              {fact.value}
            </dd>
            {fact.sub && <dd className="text-[11px] text-glass-500 truncate">{fact.sub}</dd>}
          </>
        );
        const cls = 'block rounded-lg bg-glass-100/5 border border-glass-200/10 px-3 py-2 min-w-0';
        return fact.href ? (
          <Link key={fact.label} to={fact.href} className={`${cls} hover:border-accent-400/40 transition-colors`} title="Open player profile">
            {body}
          </Link>
        ) : (
          <div key={fact.label} className={cls}>
            {body}
          </div>
        );
      })}
    </dl>
  );
};
